"use client";

import { useState, useEffect } from "react";
import { useAddressStore } from "@/store/address.store";
import { DEFAULT_COORDS } from "@/lib/geo";

interface GeolocationState {
  latitude: number | null;
  longitude: number | null;
  error: string | null;
  loading: boolean;
}

export function useGeolocation(autoRequest = true) {
  const guestAddress = useAddressStore((s) => s.guestAddress);
  // The user's chosen delivery address (set explicitly, or seeded from their
  // selected saved address) always wins over the device GPS — for guests AND
  // logged-in users — so the header label and the store list never disagree.
  const guestCoords = guestAddress
    ? { latitude: guestAddress.latitude, longitude: guestAddress.longitude }
    : null;

  const [state, setState] = useState<GeolocationState>({
    latitude: null,
    longitude: null,
    error: null,
    loading: false,
  });

  const requestLocation = () => {
    if (!navigator.geolocation) {
      setState((s) => ({ ...s, error: "Geolocation not supported", loading: false }));
      return;
    }

    setState((s) => ({ ...s, loading: true, error: null }));

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setState({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          error: null,
          loading: false,
        });
      },
      (err) => {
        setState({
          latitude: null,
          longitude: null,
          error: err.message,
          loading: false,
        });
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 }
    );
  };

  useEffect(() => {
    if (!autoRequest) return;

    // Check existing permission first to avoid prompting unnecessarily
    if ("permissions" in navigator) {
      navigator.permissions.query({ name: "geolocation" }).then((result) => {
        if (result.state === "granted" || result.state === "prompt") {
          requestLocation();
        } else {
          // Denied — leave GPS unset so guest/default coords are used
          setState({ latitude: null, longitude: null, error: "Permission denied", loading: false });
        }
      });
      return;
    }

    const id = setTimeout(requestLocation, 0);
    return () => clearTimeout(id);
  }, [autoRequest]);

  // Priority: manually chosen guest address → browser GPS → Algiers default
  const coords =
    guestCoords ??
    (state.latitude !== null && state.longitude !== null
      ? { latitude: state.latitude, longitude: state.longitude }
      : DEFAULT_COORDS);

  return { ...state, coords, requestLocation };
}
